
/**
 * Module dependencies.
 */

import { CollectionProps } from 'src/types/collection';
import { fetchCollections, paginationLimit } from './fetch-collections';
import { useInfiniteQuery } from 'react-query';
import { useRouter } from 'next/router';
import isEmpty from 'lodash/isEmpty';

/**
 * Export `InitialData` type.
 */

export type InitialData = {
  pageParams: number[],
  pages: Array<{
    data: CollectionProps[],
    nextPage: number | null,
    total: number
  }>
};

/**
 * `Options` type.
 */

type Options = {
  filterParams: any,
  initialData?: InitialData | null,
  isRecommend?: boolean
};

/**
 * Action type.
 */

const actionType = ({ isRecommend, locale }): string => {
  if (isRecommend) {
    return `${locale}-recommend-collections`;
  }

  return `${locale}-collections`;
};
/**
 * `useCollections` hook.
 */

function useCollections({ filterParams, initialData, isRecommend }: Options) {
  const { locale } = useRouter();
  const queryKey = [actionType({ isRecommend, locale }), JSON.stringify(filterParams ?? {})];
  const result = useInfiniteQuery(
    queryKey,
    ({ pageParam = 1 }) => fetchCollections({
      locale,
      params: {
        limit: paginationLimit,
        page: pageParam,
        sort: isEmpty(filterParams) ? 'sort_order' : 'name',
        ...filterParams
      }
    }), {
      getNextPageParam: (lastPage, allPages) => {
        const pageLimit = Math.ceil(allPages[0]?.total / paginationLimit);
        const nextPage = lastPage?.nextPage;

        return nextPage <= pageLimit ? nextPage : false;
      },
      initialData
    }
  );

  return result;
}

/**
 * Export `useCollections` hook.
 */

export default useCollections;
